import './TicketHeader.less';

import clsx from 'clsx';
import i18n from '@i18n';
import PopupNg from '@/ui/PopupNg';
import React, { memo } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';
import { useTicket } from '@/mytickets/components/ticket/Ticket';
import { formatMoney } from '@/common/client';
import { Cancelled, Lost, PaidOut, Registered, Won } from '@/mytickets/constants/ticketStatus';

import Cashin from './Cashin';
import { DialogCloseButton } from '@/ui/dialogs/Dialog';
import { ArrowButton, BackButton } from '@/common/components/HelperButtons';
import { BetCounts, BetCountsLoader } from '@/mytickets/components/bet-counts/BetCounts';
import CashinClosingModeButton from 'nike-ui/src/components/ticketus/cashin/CashinClosingModeButton';

import { accountUserData$ } from '@/account/subs';
import { cashinActivateClosingMode } from 'nike-ui/src/components/ticketus/handlers';
import { cashinClosingModeActive$ } from 'nike-ui/src/components/ticketus/subs';
import { isProfileCashinClosingEnabled } from 'nike-ui/src/profiles';
import { clearTicketHashOpenFromChat } from '@/forum/chat/handlersAndSubs';

const WinRow = ({ win, winText, isSaved }) =>
    isSaved ? (
        <div className={'py-8'}>
            <div className="fs-16 uppercase lh-1-5" data-atid="ticket-header-win">
                {i18n('ticketus.ticket.vyhra.possible-win')} {win}
            </div>
            <div className="fs-14">{i18n('ticketus.ticket.vyhra.if-bet-ticket')}</div>
        </div>
    ) : (
        <div className="big" data-atid="ticket-header-win">
            {winText} {win}
        </div>
    );

const TicketHeader = memo(({ ticket, headerInDialog = false }) => {
    const { subscribe } = useReframe();
    const { idZak: customerId } = subscribe(accountUserData$);
    const { alias, odds, stake, status } = useTicket(ticket, null, customerId, true);
    const saved = ticket.source === 'Saved';
    const isCustomerTicket = customerId === ticket.customerId;
    const ticketNumber = ticket.ticketNoVisual;
    const winText = getWinText(status, ticket.winningMethod);
    const win = getWinAmount(status, ticket.win, ticket.paidOut, ticket.stake);
    const { bets, betStatusCounts } = ticket;
    const cashinClosingModeActive = subscribe(cashinClosingModeActive$(ticket.ticketId));
    const isCashinProfileActive = isProfileCashinClosingEnabled();

    const handleCashinCloseButtonClick = ticketId => ref => e => {
        e.stopPropagation();
        dispatch(cashinActivateClosingMode(ticketId));
    };

    const handleAutoCashinWarning = ref => e => {
        PopupNg.dispatcher.open('dialog/warning', {
            className: 'warningText',
            isIcon: true,
            titleText: i18n('ticketus.cashin.closing.popup.warning.title'),
            bodyText: i18n('ticketus.cashin.closing.popup.warning.text'),
            cancelText: i18n('ticketus.cashin.closing.popup.close')
        });
    };

    const handleClickInfo = ref => e => {
        e.stopPropagation();
        PopupNg.dispatcher.open('dialog/article', { articleCode: 'uzatvorenie-tipu-info', type: 'marketing' }, 2);
    };

    const closeDialogHeader = e => {
        e.stopPropagation();
        dispatch(clearTicketHashOpenFromChat);
        PopupNg.dispatcher.close();
    };

    return cashinClosingModeActive ? null : (
        <>
            <div className={clsx('ticket-header', { 'px-10 py-20': !cashinClosingModeActive })}>
                <div className="close-button-section">
                    {headerInDialog ? null : <BackButton button={<ArrowButton />} />}
                </div>
                <div className="ticket-numbers-section">
                    {saved && (alias || ticketNumber) ? (
                        <div className="row">
                            <div className="huge">{alias || ticketNumber}</div>
                        </div>
                    ) : null}
                    <div className="row">
                        <WinRow win={win} winText={winText} isSaved={saved} />
                        {bets ? <BetCounts betStatusCounts={betStatusCounts} /> : <BetCountsLoader />}
                    </div>
                    <div className="row">
                        <span className="small" data-atid="ticket-header-stake">
                            {i18n('Vklad')}:{' '}
                            <b>
                                {stake} {ticket.pointsStake && `(${formatMoney(ticket.stake, 'EUR')})`}
                            </b>
                        </span>
                        <span className="small" data-atid="ticket-header-odds">
                            {i18n('Kurz')}: <b>{odds}</b>
                        </span>
                        <span className="small disappearing">{i18n('matches')}</span>
                    </div>
                </div>
                <div className="flex-fill" />
                <div className="bet-counts-section" />
                {headerInDialog ? (
                    <div>
                        <DialogCloseButton onClose={e => closeDialogHeader(e)} />
                    </div>
                ) : null}
            </div>
            <div>{headerInDialog ? null : <Cashin ticketId={ticket.ticketId} />}</div>
            {!isCustomerTicket || saved || headerInDialog || !isCashinProfileActive ? null : (
                <div className="cashin-closing-button-background full-width flex justify-center pb-20">
                    <CashinClosingModeButton
                        ticketId={ticket.ticketId}
                        handleClick={handleCashinCloseButtonClick(ticket.ticketId)}
                        handleClickInfo={handleClickInfo}
                        handleClickAutoCashin={handleAutoCashinWarning}
                    />
                </div>
            )}
        </>
    );
});
export default TicketHeader;

export function getWinText(status, winningMethod) {
    if (winningMethod === 'ReturnedBet' || winningMethod === 'CancelledBet') {
        return i18n('ticketus.ticket.vyhra.vratenyVklad');
    }
    switch (status) {
        case Registered:
        case Won:
            return i18n('EVENT_WIN');
        case Cancelled:
            return i18n('ticketus.ticket.vyhra.vratenyVklad');
        default:
            return i18n('ticketus.ticket.vyhra.vyhra');
    }
}

function getWinAmount(status, win, paidOut, stake) {
    let money = win;
    let decPlaces;
    if (status === Lost) {
        money = decPlaces = 0;
    } else if (status === Cancelled) {
        money = stake;
    } else if (status === PaidOut) {
        money = paidOut;
    }
    return formatMoney(money, 'EUR', decPlaces);
}
